import type { JSONContent } from "@tiptap/core";
import { normalizeTextColor } from "../../lib/richtext";
import { blockId, DocumentBlock, ParagraphBlock, ParagraphStyle, TextStyle } from "./types";

export function inlineContent(text: string, styles: TextStyle[] = []): JSONContent[] {
  if (styles.length) {
    const boundaries = [...new Set([0, text.length, ...styles.flatMap(style => [style.from, style.to])])]
      .filter(offset => Number.isInteger(offset) && offset >= 0 && offset <= text.length).sort((a, b) => a - b);
    return boundaries.slice(0, -1).flatMap((from, index) => {
      const to = boundaries[index + 1];
      const active = styles.filter(style => style.from <= from && style.to >= to);
      const color = active.find(style => /^#[0-9a-f]{6}$/i.test(style.color || ""))?.color;
      const marks = [
        ...(["bold", "italic", "underline", "code", "strike"] as const).filter(mark => active.some(style => style[mark])).map(type => ({ type })),
        ...active.filter(style => /^https?:\/\/\S+$/i.test(style.link || "")).slice(0, 1).map(style => ({ type: "link", attrs: { href: style.link, target: "_blank", rel: "noopener noreferrer" } })),
        ...(color ? [{ type: "textColor", attrs: { color } }] : []),
      ];
      return inlineContent(text.slice(from, to)).map(node => node.type === "text" ? { ...node, marks } : node);
    });
  }
  const lines = text.split("\n");
  return lines.flatMap((line, index) => [
    ...(index ? [{ type: "hardBreak" }] : []),
    ...(line ? [{ type: "text", text: line }] : []),
  ]);
}

function paragraphNode(block: ParagraphBlock, type = "paragraph", attrs: Record<string, unknown> = {}): JSONContent {
  return {
    type,
    attrs: { blockId: block.id, origin: block.origin || "USER", ...attrs },
    content: inlineContent(block.text, block.formatting?.text),
  };
}

function listNode(blocks: ParagraphBlock[], ordered: boolean): JSONContent {
  return {
    type: ordered ? "orderedList" : "bulletList",
    content: blocks.map((block) => ({
      type: "listItem",
      content: [paragraphNode(block)],
    })),
  };
}

export function blocksToTiptap(blocks: DocumentBlock[], imageSources: Record<string, string> = {}): JSONContent {
  const content: JSONContent[] = [];
  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];
    if (block.type === "PARAGRAPH") {
      const style = block.style || "BODY";
      if (style === "BULLET" || style === "NUMBERED") {
        const grouped: ParagraphBlock[] = [block];
        while (blocks[index + 1]?.type === "PARAGRAPH" && (blocks[index + 1] as ParagraphBlock).style === style) {
          grouped.push(blocks[index + 1] as ParagraphBlock);
          index += 1;
        }
        content.push(listNode(grouped, style === "NUMBERED"));
      } else if (style === "HEADING_1" || style === "HEADING_2" || style === "HEADING_3") {
        content.push(paragraphNode(block, "heading", { level: Number(style.slice(-1)) }));
      } else if (style === "QUOTE") {
        content.push({ type: "blockquote", content: [paragraphNode(block)] });
      } else {
        content.push(paragraphNode(block));
      }
      continue;
    }
    if (block.type === "TABLE") {
      content.push({
        type: "table",
        attrs: { blockId: block.id, origin: block.origin || "USER", caption: block.caption || "" },
        content: [
          { type: "tableRow", content: block.columns.map((column, cell) => ({ type: "tableHeader", content: [{ type: "paragraph", content: inlineContent(column, block.formatting?.[`0:${cell}`]) }] })) },
          ...block.rows.map((row, rowIndex) => ({ type: "tableRow", content: block.columns.map((_, cell) => ({ type: "tableCell", content: [{ type: "paragraph", content: inlineContent(row[cell] || "", block.formatting?.[`${rowIndex + 1}:${cell}`]) }] })) })),
        ],
      });
      continue;
    }
    if (block.type === "IMAGE") {
      content.push({
        type: "image",
        attrs: {
          src: imageSources[block.file.id] || "",
          alt: block.altText || "",
          title: block.caption || "",
          blockId: block.id,
          origin: block.origin || "USER",
          fileId: block.file.id,
          originalName: block.file.originalName,
          contentType: block.file.contentType,
          sizeBytes: block.file.sizeBytes,
          sha256: block.file.sha256,
          width: block.width || null,
          height: block.height || null,
        },
      });
      continue;
    }
    content.push({
      type: "attachment",
      attrs: {
        blockId: block.id, origin: block.origin || "USER", fileId: block.file.id,
        originalName: block.file.originalName, contentType: block.file.contentType,
        sizeBytes: block.file.sizeBytes, sha256: block.file.sha256, caption: block.caption || "",
      },
    });
  }
  return { type: "doc", content: content.length ? content : [{ type: "paragraph" }] };
}

function nodeText(node: JSONContent): string {
  if (node.type === "hardBreak") return "\n";
  if (node.type === "text") return node.text || "";
  return (node.content || []).map(nodeText).join("");
}

function nodeStyles(node: JSONContent): TextStyle[] {
  let offset = 0;
  const styles: TextStyle[] = [];
  function visit(current: JSONContent) {
    if (current.type === "hardBreak") { offset += 1; return; }
    if (current.type !== "text") { current.content?.forEach(visit); return; }
    const from = offset;
    offset += current.text?.length || 0;
    const marks = current.marks || [];
    const color = marks.find(mark => mark.type === "textColor")?.attrs?.color as string | undefined;
    const hex = normalizeTextColor(color);
    const style: TextStyle = { from, to: offset };
    for (const type of ["bold", "italic", "underline", "code", "strike"] as const) if (marks.some(mark => mark.type === type)) style[type] = true;
    const link = marks.find(mark => mark.type === "link")?.attrs?.href;
    if (typeof link === "string" && /^https?:\/\/\S+$/i.test(link)) style.link = link;
    if (hex && /^#[0-9a-f]{6}$/i.test(hex)) style.color = hex;
    if (Object.keys(style).length > 2) styles.push(style);
  }
  visit(node);
  return styles;
}

function paragraphFromNode(node: JSONContent, style: ParagraphStyle = "BODY"): ParagraphBlock {
  const formatting = nodeStyles(node);
  return {
    id: String(node.attrs?.blockId || blockId()),
    type: "PARAGRAPH",
    text: nodeText(node),
    style,
    origin: node.attrs?.origin === "AI" ? "AI" : "USER",
    ...(formatting.length ? { formatting: { text: formatting } } : {}),
  };
}

function ensureUniqueBlockIds(blocks: DocumentBlock[]): DocumentBlock[] {
  const seen = new Set<string>();
  return blocks.map((block) => {
    if (block.id && !seen.has(block.id)) {
      seen.add(block.id);
      return block;
    }
    let id = blockId();
    while (seen.has(id)) id = blockId();
    seen.add(id);
    return { ...block, id };
  });
}

export function tiptapToBlocks(document: JSONContent): DocumentBlock[] {
  const blocks: DocumentBlock[] = [];
  for (const node of document.content || []) {
    if (node.type === "paragraph") blocks.push(paragraphFromNode(node));
    else if (node.type === "heading") blocks.push(paragraphFromNode(node, node.attrs?.level === 1 ? "HEADING_1" : node.attrs?.level === 2 ? "HEADING_2" : "HEADING_3"));
    else if (node.type === "blockquote") {
      for (const child of node.content || []) blocks.push(paragraphFromNode(child, "QUOTE"));
    } else if (node.type === "bulletList" || node.type === "orderedList") {
      for (const item of node.content || []) {
        const paragraph = item.content?.find((child) => child.type === "paragraph");
        if (paragraph) blocks.push(paragraphFromNode(paragraph, node.type === "bulletList" ? "BULLET" : "NUMBERED"));
      }
    } else if (node.type === "table") {
      const rows = node.content || [];
      const cells = (row: JSONContent) => (row.content || []).map(nodeText);
      const columns = rows[0] ? cells(rows[0]) : ["항목"];
      const formatting = Object.fromEntries(rows.flatMap((row, rowIndex) => (row.content || [])
        .map((cell, cellIndex) => [`${rowIndex}:${cellIndex}`, nodeStyles(cell)] as const).filter(([, styles]) => styles.length)));
      blocks.push({
        id: String(node.attrs?.blockId || blockId()), type: "TABLE", columns,
        rows: rows.slice(1).map(cells), caption: String(node.attrs?.caption || ""),
        origin: node.attrs?.origin === "AI" ? "AI" : "USER",
        ...(Object.keys(formatting).length ? { formatting } : {}),
      });
    } else if (node.type === "image" && node.attrs?.fileId) {
      blocks.push({
        id: String(node.attrs.blockId || blockId()), type: "IMAGE",
        file: {
          id: String(node.attrs.fileId), originalName: String(node.attrs.originalName || "image"),
          contentType: String(node.attrs.contentType || "image/*"), sizeBytes: Number(node.attrs.sizeBytes || 0),
          sha256: String(node.attrs.sha256 || ""),
        },
        caption: String(node.attrs.title || ""), altText: String(node.attrs.alt || ""),
        width: node.attrs.width ? Number(node.attrs.width) : undefined,
        height: node.attrs.height ? Number(node.attrs.height) : undefined,
        origin: node.attrs.origin === "AI" ? "AI" : "USER",
      });
    } else if (node.type === "attachment" && node.attrs?.fileId) {
      blocks.push({
        id: String(node.attrs.blockId || blockId()), type: "FILE",
        file: {
          id: String(node.attrs.fileId), originalName: String(node.attrs.originalName || "file"),
          contentType: String(node.attrs.contentType || "application/octet-stream"), sizeBytes: Number(node.attrs.sizeBytes || 0),
          sha256: String(node.attrs.sha256 || ""),
        },
        caption: String(node.attrs.caption || ""), origin: node.attrs.origin === "AI" ? "AI" : "USER",
      });
    }
  }
  return ensureUniqueBlockIds(blocks.length ? blocks : [{ id: blockId(), type: "PARAGRAPH", text: "", style: "BODY", origin: "USER" }]);
}
